import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";

// Country code to flag image URL using flagcdn.com (reliable SVG flags)
const getFlagUrl = (code: string) => {
  const countryMap: Record<string, string> = {
    de: "at", en: "gb", fr: "fr", hu: "hu",
    ro: "ro", es: "es", it: "it", ru: "ru", zh: "cn", ar: "sa",
  };
  const country = countryMap[code] || code;
  return `https://flagcdn.com/w40/${country}.png`;
};

const FlagImg = ({ code, size = 20 }: { code: string; size?: number }) => (
  <img
    src={getFlagUrl(code)}
    alt={code.toUpperCase()}
    width={size}
    height={Math.round(size * 0.75)}
    className="rounded-sm object-cover"
    style={{ width: size, height: Math.round(size * 0.75) }}
    loading="lazy"
  />
);

const LanguageSwitcher = ({ mobile = false }: { mobile?: boolean }) => {
  const { currentLang, setCurrentLang, enabledLanguages, featureEnabled, isTranslating } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!featureEnabled || enabledLanguages.length <= 1) return null;

  const current = enabledLanguages.find((l) => l.code === currentLang) || enabledLanguages[0];

  if (mobile) {
    return (
      <div className="space-y-2" data-no-translate>
        <p className="text-xs font-body font-bold text-muted-foreground uppercase tracking-wide py-1">Jezik / Language</p>
        <div className="flex flex-wrap gap-2">
          {enabledLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setCurrentLang(lang.code)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body transition-all ${
                lang.code === currentLang
                  ? "bg-primary/10 text-primary font-bold ring-1 ring-primary/20"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <FlagImg code={lang.code} size={20} />
              <span className="font-bold">{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-no-translate>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2.5 py-1.5 text-sm font-body font-bold text-foreground hover:text-primary rounded-lg transition-colors"
        title="Izaberite jezik"
      >
        {isTranslating ? (
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        ) : (
          <FlagImg code={current.code} size={22} />
        )}
        <span className="font-bold tracking-wide">{current.code.toUpperCase()}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 mt-2 bg-card rounded-xl shadow-card-hover border border-border/50 py-1.5 min-w-[180px] z-50"
          >
            {enabledLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setCurrentLang(lang.code); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm font-body flex items-center gap-3 transition-colors ${
                  lang.code === currentLang
                    ? "text-primary font-bold bg-primary/5"
                    : "text-foreground hover:text-primary hover:bg-muted/50"
                }`}
              >
                <FlagImg code={lang.code} size={22} />
                <span className="font-bold">{lang.code.toUpperCase()}</span>
                <span className="text-muted-foreground text-xs ml-auto">{lang.name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
